import axios from 'axios';
import { extractApiMessage } from './apiError';
import { showToast } from '../components/ui/toast.utils';

declare module 'axios' {
  export interface AxiosRequestConfig {
    skipErrorToast?: boolean;
  }
}

/** En HTTPS (ej. qms.microsyst.com.ar) la API va por el reverse proxy en /api. */
function resolveApiBaseUrl(): string {
  if (import.meta.env.DEV) return '/api';
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    return '/api';
  }
  const configured = import.meta.env.VITE_API_URL?.replace(/\/$/, '');
  return configured ? `${configured}/api` : '/api';
}

const API_URL = resolveApiBaseUrl();

// Crear instancia de axios con configuración personalizada
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para requests - añadir el token de autorización automáticamente
const addAuthInterceptor = (apiInstance: typeof api) => {
  apiInstance.interceptors.request.use(
    config => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    error => Promise.reject(error)
  );
};

// Interceptor para responses - manejar errores de autorización
const addResponseInterceptor = (apiInstance: typeof api) => {
  apiInstance.interceptors.response.use(
    response => response,
    error => {
      const status = error.response?.status;
      if (status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      const method = error.config?.method?.toUpperCase();
      const isMutation = method && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
      if (isMutation && !error.config?.skipErrorToast) {
        showToast(extractApiMessage(error), 'error');
      }

      return Promise.reject(error);
    }
  );
};

// Aplicar interceptors
addAuthInterceptor(api);
addResponseInterceptor(api);

export default api;
